import React, { useState, useEffect, useCallback } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import UserTypeModal from "./UserTypeModal.js";
import { makeAuthenticatedRequest } from "../hooks/useAuth.js";
import { useFeatureFlag } from "../features/featureFlags/useFeatureFlags.js";
import AdopterCoursePrompt from "../features/courses/components/AdopterCoursePrompt.js";

/**
 * Manager component that controls when to show the UserTypeModal
 * Shows modal only when:
 * 1. User is authenticated
 * 2. User hasn't answered the modal before (userType is null)
 */
const UserTypeModalManager: React.FC = () => {
  const {
    isAuthenticated,
    isLoading: authLoading,
    getAccessTokenSilently,
  } = useAuth0();
  const [showModal, setShowModal] = useState(false);
  const [userType, setUserType] = useState<string | null>(null);
  const [isChecking, setIsChecking] = useState(true);
  const [hasChecked, setHasChecked] = useState(false);
  const coursesEnabled = useFeatureFlag("courses");
  // Shown once per account (server-stored dismissal), only to adopters, never on top of the type modal.
  const [coursePromptPending, setCoursePromptPending] = useState(false);

  // Check user's profile when authenticated
  useEffect(() => {
    const checkUserType = async () => {
      // Only run once when authenticated
      if (!isAuthenticated || authLoading || hasChecked) {
        if (!isAuthenticated && !authLoading) {
          setIsChecking(false);
        }
        return;
      }

      try {
        setIsChecking(true);

        // Fetch user settings to check if userType is set
        const response = await makeAuthenticatedRequest(
          "/api/user/settings",
          { method: "GET" },
          getAccessTokenSilently,
        );

        if (response.ok) {
          const data = await response.json();
          const currentUserType = data.user?.userType;

          setUserType(currentUserType);
          const promptDismissed = Boolean(data.user?.adopterCoursePromptDismissedAt);
          setCoursePromptPending(!promptDismissed && (currentUserType === "adopter" || !currentUserType));

          // Show modal only if userType is not set
          if (!currentUserType) {
            setShowModal(true);
          }
        }
      } catch (error) {
        console.error("Error checking user type:", error);
      } finally {
        setIsChecking(false);
        setHasChecked(true);
      }
    };

    checkUserType();
  }, [isAuthenticated, authLoading, hasChecked, getAccessTokenSilently]);

  // Handle user type selection
  const handleSelectType = async (type: "adopter" | "breeder" | "visitor") => {
    try {
      const response = await makeAuthenticatedRequest(
        "/api/user/settings",
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            userType: type,
          }),
        },
        getAccessTokenSilently,
      );

      if (response.ok) {
        setUserType(type);
        setShowModal(false);
        if (type !== "adopter") setCoursePromptPending(false);
        // console.log(`User type set to: ${type}`);
      } else {
        console.error("Failed to update user type");
      }
    } catch (error) {
      console.error("Error updating user type:", error);
    }
  };

  const handleClose = () => {
    // Allow closing the modal, but it will show again on next login
    // unless the user has selected a type
    setShowModal(false);
  };

  // Don't render anything while checking or if not authenticated
  if (isChecking || !isAuthenticated || authLoading) {
    return null;
  }

  const showCoursePrompt =
    coursesEnabled && coursePromptPending && !showModal && userType === "adopter";

  return (
    <>
      <UserTypeModal
        isOpen={showModal}
        onClose={handleClose}
        onSelectType={handleSelectType}
      />
      {showCoursePrompt && <AdopterCoursePrompt onDone={() => setCoursePromptPending(false)} />}
    </>
  );
};

export default UserTypeModalManager;
